import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSsoCallback } from '@foundation-bb/foundation-auth-ui';
import { Spinner } from '@/ui';
import { setAuth, setMe } from '@/store/authSlice';
import { rysApi } from '@/api/rysApi';
import type { AppDispatch } from '@/store/store';
import * as layout from '@/styles/layout.css';
import * as authStatus from './authStatus.css';

// The SSO callback Function redirects here with access_token (+ access_expires)
// in the URL fragment on success; the HttpOnly refresh cookie is set by the API
// and never visible to JS. On failure the API redirects with ?error=<code>.
// Foundation's useSsoCallback parses both; a missing token surfaces as the
// `auth.sso_exchange_failed` code. We apply the token, load /me for the display
// name, then navigate — or route to the styled AuthErrorPage on any failure.
export default function SsoCallbackPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useSsoCallback({
    onAuthenticated: ({ accessToken }) => {
      void (async () => {
        try {
          dispatch(setAuth({ accessToken }));
          const me = await dispatch(
            rysApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }),
          ).unwrap();
          dispatch(setMe(me));
          navigate('/dashboard', { replace: true });
        } catch {
          navigate(`/auth/error?code=${encodeURIComponent('auth.sso_exchange_failed')}`, { replace: true });
        }
      })();
    },
    onError: (code) => {
      navigate(`/auth/error?code=${encodeURIComponent(code)}`, { replace: true });
    },
  });

  return (
    <div className={layout.centerBoth} style={{ minHeight: '100vh' }}>
      <Spinner size="sm" /> <span className={authStatus.spinnerLabel}>{t('callback.loading')}</span>
    </div>
  );
}
